(() => {
  if (window.__mkoCartUiReadyV3) return;
  window.__mkoCartUiReadyV3 = true;

  const THRESHOLD = 9999;
  const money = new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  });

  let refreshTimer;
  let lastOpen = false;
  let pendingDrawerOpen = null;
  let crossSellRequestId = 0;

  const recommendationCache = new Map();
  const trackedCrossSellViews = new Set();

  const formatMoney = (cents) =>
    money.format((Number(cents) || 0) / 100);

  const publish = (name, detail = {}) => {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: `mko_${name}`, ...detail });
    } catch (_) {}

    try {
      const result = window.Shopify?.analytics?.publish?.(
        `mko:${name}`,
        detail
      );
      if (result?.catch) result.catch(() => {});
    } catch (_) {}
  };

  const getCart = async () => {
    try {
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (_) {
      return null;
    }
  };

  const suppressNotification = () => {
    const panel = document.getElementById('cartNotificationPanel');
    if (!panel) return;

    if (panel.dataset.mkoSuppressed !== 'true') {
      panel.dataset.mkoSuppressed = 'true';
      panel.open = function () {
        this.setAttribute('hidden', 'true');
        this.setAttribute('aria-hidden', 'true');
      };
    }

    panel.setAttribute('hidden', 'true');
    panel.setAttribute('aria-hidden', 'true');
  };

  const ensureProgress = (drawer) => {
    const body = drawer.querySelector('.drawer__body');
    if (!body || drawer.querySelector('.mko-cart-progress')) return;

    const nativeProgress = body.querySelector('.cart-drawer-shipping');
    if (nativeProgress) nativeProgress.hidden = true;

    const box = document.createElement('div');
    box.className = 'mko-cart-progress';
    box.innerHTML =
      '<strong class="mko-cart-progress__message"></strong>' +
      '<div class="mko-cart-progress__track" aria-hidden="true">' +
      '<span class="mko-cart-progress__bar"></span></div>' +
      '<small class="mko-cart-progress__note">Spedizione tracciata e reso entro 14 giorni.</small>';
    body.prepend(box);
  };

  const ensureFooter = (drawer) => {
    const footer = drawer.querySelector('.cart-drawer__footer');
    if (!footer) return;

    if (!footer.querySelector('.mko-cart-assurance')) {
      const assurance = document.createElement('div');
      assurance.className = 'mko-cart-assurance';
      assurance.textContent =
        'Checkout sicuro. Il totale finale viene confermato prima del pagamento.';
      footer.appendChild(assurance);
    }
  };

  const getImageUrl = (source) => {
    if (!source) return '';
    if (typeof source === 'string') return source;
    return source.url || source.src || source.original_src || '';
  };

  const getAvailableVariants = (product) => {
    const variants = Array.isArray(product?.variants)
      ? product.variants
      : [];
    return variants.filter((variant) => variant.available !== false);
  };

  const fetchRecommendations = async (productId, cartProductIds) => {
    const cacheKey = `${productId}:${[...cartProductIds].sort().join(',')}`;
    if (recommendationCache.has(cacheKey)) {
      return recommendationCache.get(cacheKey);
    }

    const intents = ['complementary', 'related'];
    let selected = null;

    for (const intent of intents) {
      try {
        const url =
          `/recommendations/products.json?product_id=${encodeURIComponent(productId)}` +
          `&limit=8&intent=${intent}`;
        const response = await fetch(url, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        });
        if (!response.ok) continue;

        const payload = await response.json();
        const products = Array.isArray(payload?.products)
          ? payload.products
          : [];

        selected = products.find((product) => {
          const id = Number(product.id);
          return (
            product.available !== false &&
            !cartProductIds.has(id) &&
            getAvailableVariants(product).length > 0
          );
        });

        if (selected) break;
      } catch (_) {}
    }

    recommendationCache.set(cacheKey, selected || null);
    return selected || null;
  };

  const setCrossSellVariant = (section, option) => {
    if (!section || !option) return;

    const price = section.querySelector(
      '.mko-cart-cross-sell__price strong'
    );
    const compare = section.querySelector(
      '.mko-cart-cross-sell__price s'
    );
    const image = section.querySelector('.mko-cart-cross-sell__image');

    if (price) price.textContent = formatMoney(option.dataset.price);

    const compareValue = Number(option.dataset.compare || 0);
    const priceValue = Number(option.dataset.price || 0);
    if (compare) {
      compare.textContent =
        compareValue > priceValue ? formatMoney(compareValue) : '';
      compare.hidden = compareValue <= priceValue;
    }

    if (image && option.dataset.image) {
      image.src = option.dataset.image;
    }
  };

  const buildCrossSell = (product, signature) => {
    const variants = getAvailableVariants(product);
    const selectedVariant = variants[0];
    if (!selectedVariant) return null;

    const section = document.createElement('section');
    section.className = 'mko-cart-cross-sell';
    section.dataset.signature = signature;
    section.dataset.productId = String(product.id || '');

    const head = document.createElement('div');
    head.className = 'mko-cart-cross-sell__head';

    const headingCopy = document.createElement('span');
    const heading = document.createElement('strong');
    heading.textContent = 'Completa il look';
    const note = document.createElement('small');
    note.textContent = 'Un solo suggerimento coerente con ciò che hai scelto.';
    headingCopy.append(heading, note);

    const badge = document.createElement('span');
    badge.className = 'mko-cart-cross-sell__badge';
    badge.textContent = 'Consigliato';
    head.append(headingCopy, badge);

    const card = document.createElement('div');
    card.className = 'mko-cart-cross-sell__card';

    const productUrl =
      product.url ||
      (product.handle ? `/products/${product.handle}` : '/collections/all');

    const imageLink = document.createElement('a');
    imageLink.className = 'mko-cart-cross-sell__image-link';
    imageLink.href = productUrl;

    const image = document.createElement('img');
    image.className = 'mko-cart-cross-sell__image';
    image.loading = 'lazy';
    image.alt = product.title || 'Prodotto consigliato';
    image.src =
      getImageUrl(selectedVariant.featured_image) ||
      getImageUrl(product.featured_image) ||
      getImageUrl(product.images?.[0]);
    imageLink.appendChild(image);

    const content = document.createElement('div');
    content.className = 'mko-cart-cross-sell__content';

    if (product.vendor) {
      const vendor = document.createElement('span');
      vendor.className = 'mko-cart-cross-sell__vendor';
      vendor.textContent = product.vendor;
      content.appendChild(vendor);
    }

    const title = document.createElement('a');
    title.className = 'mko-cart-cross-sell__title';
    title.href = productUrl;
    title.textContent = product.title || 'Prodotto consigliato';
    content.appendChild(title);

    const priceWrap = document.createElement('div');
    priceWrap.className = 'mko-cart-cross-sell__price';
    const currentPrice = document.createElement('strong');
    const comparePrice = document.createElement('s');
    priceWrap.append(currentPrice, comparePrice);
    content.appendChild(priceWrap);

    const actions = document.createElement('div');
    actions.className = 'mko-cart-cross-sell__actions';

    const select = document.createElement('select');
    select.className = 'mko-cart-cross-sell__select';
    select.setAttribute('aria-label', 'Scegli la variante del prodotto consigliato');
    select.dataset.mkoCrossSellSelect = 'true';

    variants.forEach((variant) => {
      const option = document.createElement('option');
      option.value = String(variant.id);
      option.textContent =
        variant.title && variant.title !== 'Default Title'
          ? variant.title
          : 'Taglia unica';
      option.dataset.price = String(variant.price || product.price || 0);
      option.dataset.compare = String(
        variant.compare_at_price || product.compare_at_price || 0
      );
      option.dataset.image =
        getImageUrl(variant.featured_image) ||
        getImageUrl(product.featured_image) ||
        '';
      select.appendChild(option);
    });

    if (
      variants.length === 1 &&
      (!variants[0].title || variants[0].title === 'Default Title')
    ) {
      select.hidden = true;
    }

    const add = document.createElement('button');
    add.type = 'button';
    add.className = 'mko-cart-cross-sell__add';
    add.dataset.mkoCrossSellAdd = 'true';
    add.textContent = 'Aggiungi';

    const error = document.createElement('small');
    error.className = 'mko-cart-cross-sell__error';
    error.hidden = true;

    actions.append(select, add);
    content.append(actions, error);
    card.append(imageLink, content);
    section.append(head, card);

    setCrossSellVariant(section, select.options[0]);
    return section;
  };

  const renderCrossSell = async (drawer, cart) => {
    const requestId = ++crossSellRequestId;
    const existing = drawer.querySelector('.mko-cart-cross-sell');

    if (!cart?.items?.length) {
      existing?.remove();
      return;
    }

    const anchor = cart.items[0];
    const cartProductIds = new Set(
      cart.items.map((item) => Number(item.product_id))
    );
    const recommendation = await fetchRecommendations(
      anchor.product_id,
      cartProductIds
    );

    if (requestId !== crossSellRequestId) return;

    if (!recommendation) {
      existing?.remove();
      return;
    }

    const signature = `${anchor.product_id}:${cart.items
      .map((item) => item.product_id)
      .join(',')}:${recommendation.id}`;

    if (existing?.dataset.signature === signature) return;
    existing?.remove();

    const section = buildCrossSell(recommendation, signature);
    const formHost = drawer.querySelector('sht-cart-drwr-frm');
    if (!section || !formHost) return;

    formHost.insertAdjacentElement('afterend', section);

    const viewKey = `${anchor.product_id}:${recommendation.id}`;
    if (!trackedCrossSellViews.has(viewKey)) {
      trackedCrossSellViews.add(viewKey);
      publish('cross_sell_view', {
        anchor_product_id: anchor.product_id,
        recommended_product_id: recommendation.id,
        recommended_product_handle: recommendation.handle || ''
      });
    }
  };

  const updateDrawer = async (drawer, reason = 'refresh') => {
    if (!drawer) return;
    const cart = await getCart();
    if (!cart) return;

    const heading = drawer.querySelector('.drawer__heading');
    if (heading) {
      heading.textContent = cart.item_count
        ? `Il tuo carrello (${cart.item_count})`
        : 'Il tuo carrello';
    }

    if (cart.item_count > 0) {
      ensureProgress(drawer);
      ensureFooter(drawer);
    }

    const progress = drawer.querySelector('.mko-cart-progress');
    if (progress) {
      const remaining = Math.max(0, THRESHOLD - cart.total_price);
      const percent = Math.min(
        100,
        Math.max(0, (cart.total_price / THRESHOLD) * 100)
      );
      progress.style.setProperty('--mko-progress', `${percent}%`);

      const message = progress.querySelector(
        '.mko-cart-progress__message'
      );
      if (message) {
        message.textContent =
          remaining > 0
            ? `Ti mancano ${formatMoney(remaining)} per la spedizione gratuita`
            : 'Hai ottenuto la spedizione gratuita';
      }
    }

    const footer = drawer.querySelector('.cart-drawer__footer');
    if (footer) {
      const saved = Math.max(
        0,
        (cart.original_total_price || cart.total_price) - cart.total_price
      );
      let savings = footer.querySelector('.mko-cart-savings');

      if (saved > 0) {
        if (!savings) {
          savings = document.createElement('div');
          savings.className = 'mko-cart-savings';
          const totalRow = footer.querySelector(
            '.d-flex.middle-xs.between-xs'
          );
          footer.insertBefore(savings, totalRow || footer.firstChild);
        }
        savings.innerHTML =
          `<span>Risparmio applicato</span><strong>${formatMoney(saved)}</strong>`;
      } else {
        savings?.remove();
      }
    }

    await renderCrossSell(drawer, cart);

    if (reason !== 'refresh') {
      publish('cart_updated', {
        reason,
        item_count: cart.item_count,
        cart_total: cart.total_price,
        currency: cart.currency || 'EUR'
      });
    }
  };

  const scheduleUpdate = (drawer, reason = 'refresh') => {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(
      () => updateDrawer(drawer, reason),
      180
    );
  };

  const openDrawerAfterNativeRender = (drawer) => {
    if (!pendingDrawerOpen || !drawer) return;

    if (Date.now() - pendingDrawerOpen.startedAt > 5000) {
      pendingDrawerOpen = null;
      return;
    }

    const hasProduct = Boolean(
      drawer.querySelector('.cart-drawer__product')
    );
    if (!hasProduct) return;

    const opener =
      pendingDrawerOpen.opener ||
      document.querySelector('.js-header-cart-status-btn');

    suppressNotification();

    window.requestAnimationFrame(() => {
      if (typeof drawer.openDrawer === 'function') {
        drawer.openDrawer(opener || drawer);
      } else {
        drawer.removeAttribute('hidden');
        drawer.setAttribute('open', '');
        drawer.setAttribute('aria-hidden', 'false');
      }

      pendingDrawerOpen = null;
      scheduleUpdate(drawer, 'add');
    });
  };

  const refreshCartSections = async (drawer) => {
    const separator = window.location.search ? '&' : '?';
    const url =
      `${window.location.pathname}${window.location.search}${separator}` +
      'sections=cart-drawer,header-cart-status';
    const response = await fetch(url, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error('Impossibile aggiornare il carrello');

    const sections = await response.json();
    const drawerForm = drawer.querySelector('sht-cart-drwr-frm');

    if (drawerForm?.renderContents) {
      drawerForm.renderContents({ sections });
    } else if (sections['cart-drawer']) {
      const parsed = new DOMParser().parseFromString(
        sections['cart-drawer'],
        'text/html'
      );
      const nextWrapper = parsed.querySelector(
        '.js-cart-drawer-wrapper'
      );
      const currentWrapper = drawer.querySelector(
        '.js-cart-drawer-wrapper'
      );
      if (nextWrapper && currentWrapper) {
        currentWrapper.innerHTML = nextWrapper.innerHTML;
      }
    }

    if (sections['header-cart-status']) {
      const parsedHeader = new DOMParser().parseFromString(
        sections['header-cart-status'],
        'text/html'
      );
      const nextHeader = parsedHeader.querySelector('#headerCartStatus');
      const currentHeader = document.querySelector('#headerCartStatus');
      if (nextHeader && currentHeader) {
        currentHeader.innerHTML = nextHeader.innerHTML;
      }
    }
  };

  const addCrossSell = async (button, drawer) => {
    const section = button.closest('.mko-cart-cross-sell');
    const select = section?.querySelector(
      '[data-mko-cross-sell-select]'
    );
    const error = section?.querySelector(
      '.mko-cart-cross-sell__error'
    );
    const variantId = Number(select?.value);

    if (!section || !variantId) return;

    button.disabled = true;
    button.textContent = 'Aggiungo…';
    if (error) error.hidden = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }]
        })
      });

      const payload = await response.json();
      if (!response.ok || payload.status) {
        throw new Error(
          payload.description || 'Non è stato possibile aggiungere il prodotto.'
        );
      }

      publish('cross_sell_add', {
        recommended_product_id: section.dataset.productId || '',
        variant_id: variantId,
        location: 'cart_drawer'
      });

      await refreshCartSections(drawer);
      suppressNotification();
      scheduleUpdate(drawer, 'cross_sell');
    } catch (exception) {
      button.disabled = false;
      button.textContent = 'Aggiungi';
      if (error) {
        error.textContent =
          exception?.message || 'Non è stato possibile aggiungere il prodotto.';
        error.hidden = false;
      }
    }
  };

  const initDrawer = () => {
    const drawer = document.getElementById('shtCartDrawer');
    if (!drawer || drawer.dataset.mkoCartBoundV3 === 'true') return;
    drawer.dataset.mkoCartBoundV3 = 'true';

    const syncOpen = () => {
      const open =
        drawer.hasAttribute('open') ||
        (!drawer.hidden && drawer.getAttribute('aria-hidden') !== 'true');

      if (open && !lastOpen) {
        scheduleUpdate(drawer, 'open');
        publish('cart_open', { location: 'drawer' });
      }
      lastOpen = open;
    };

    const observer = new MutationObserver((mutations) => {
      syncOpen();

      if (mutations.some((mutation) => mutation.type === 'childList')) {
        openDrawerAfterNativeRender(drawer);
        scheduleUpdate(drawer);
      }
    });

    observer.observe(drawer, {
      attributes: true,
      attributeFilter: ['hidden', 'aria-hidden', 'open'],
      childList: true,
      subtree: true
    });

    drawer.addEventListener('click', (event) => {
      const target = event.target instanceof Element
        ? event.target
        : null;
      if (!target) return;

      const crossSellButton = target.closest(
        '[data-mko-cross-sell-add]'
      );
      if (crossSellButton) {
        event.preventDefault();
        addCrossSell(crossSellButton, drawer);
        return;
      }

      if (target.closest('.js-cart-drawer-quantity-btn')) {
        scheduleUpdate(drawer, 'quantity');
      }
      if (target.closest('.js-cart-drawer-delete-btn')) {
        scheduleUpdate(drawer, 'remove');
      }
      if (target.closest('button[name="checkout"]')) {
        publish('checkout_click', { location: 'cart_drawer' });
      }
      if (target.closest('a[href*="/cart"]')) {
        publish('view_cart_click', { location: 'cart_drawer' });
      }
    });

    drawer.addEventListener('change', (event) => {
      const target = event.target instanceof Element
        ? event.target
        : null;
      if (!target) return;

      if (target.matches('.js-cart-drawer-quantity-input')) {
        scheduleUpdate(drawer, 'quantity');
      }

      if (target.matches('[data-mko-cross-sell-select]')) {
        setCrossSellVariant(
          target.closest('.mko-cart-cross-sell'),
          target.selectedOptions?.[0]
        );
      }
    });

    syncOpen();
  };

  const bindAddToCartFlow = () => {
    if (document.documentElement.dataset.mkoCartSubmitBound === 'true') {
      return;
    }
    document.documentElement.dataset.mkoCartSubmitBound = 'true';

    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target instanceof HTMLFormElement
          ? event.target
          : null;
        if (!form || !form.matches('form[action*="/cart/add"]')) return;

        pendingDrawerOpen = {
          opener:
            event.submitter ||
            form.querySelector('[type="submit"]') ||
            document.querySelector('.js-header-cart-status-btn'),
          startedAt: Date.now()
        };
        suppressNotification();
      },
      true
    );
  };

  const init = () => {
    suppressNotification();
    bindAddToCartFlow();
    initDrawer();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
